import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Grid,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  Paper,
  Card,
  CardContent,
  Divider,
  Chip,
  Container,
} from "@mui/material";
import { apiUrl } from "../../../constants";
import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PhoneIcon from "@mui/icons-material/Phone";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const GetQRTickets = () => {
  const [ticketId, setTicketId] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [ticketCount, setTicketCount] = useState(1);
  const [allTickets, setAllTickets] = useState([]);
  const [shortIdByPhone, setShortIdByPhone] = useState();
  const [qrTicketsData, setQrTicketsData] = useState({
    tickets: [],
    total_coins: 0,
  });
  const [error, setError] = useState(null);
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    message: "",
  });
  const { token } = useSelector((state) => state.userSlice);
  const [current, setCurrent] = useState(0);

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDialogClose = () => {
    setQrTicketsData({ total_coins: 0, tickets: [] });
    setTicketId("");
    setPhoneNo("");
    setTicketCount(0);
    setCurrent(0);
    setError(null);
  };

  const handleShortIdByPhone = async ({ phone_no, count }) => {
    let phoneNumber = "+91-" + phone_no;
    const shortIdres = await axios.post(
      `${apiUrl}/ticket/phone_numberticket`,
      {
        phone_no: phoneNumber,
        count,
      },
      {
        headers: {
          token,
        },
      },
    );
    if (shortIdres.data?.short_id) {
      setShortIdByPhone(shortIdres.data.short_id);
    } else {
      console.log(shortIdres.data.message);
    }
  };

  const handleGenerateQR = async ({ phone_no, ticket_count, ticket_id }) => {
    try {
      setError(null);
      setAllTickets([]);
      const resp = await axios.get(`${apiUrl}/ticket/get-qr-tickets`, {
        params: {
          phone_no: phone_no ? "+91-" + phone_no : undefined,
          number_of_tickets: ticket_count,
          ticket_id,
        },
        headers: {
          token,
        },
      });
      setQrTicketsData({
        total_coins: resp.data.total_coins,
        tickets: resp.data.tickets,
      });
    } catch (err) {
      setError(err.response.data);
    }
  };

  const onNext = () => {
    setCurrent((curr) => Math.min(qrTicketsData.tickets.length - 1, curr + 1));
  };

  const onPrev = () => {
    setCurrent((curr) => Math.max(0, curr - 1));
  };

  useEffect(() => {
    setTicketId(params.get("tid") || "");
  }, []);

  const getDisplayText = () => {
    return (
      qrTicketsData.tickets?.[current]?.ticket_identifier ||
      ticketId ||
      shortIdByPhone ||
      "NA"
    );
  };

  const handleTicketIdGenerate = () => {
    if (!ticketId || ticketId.trim() === "") {
      setValidationDialog({
        open: true,
        message: "Please enter a Ticket ID to generate QR tickets",
      });
      return;
    }
    handleGenerateQR({ ticket_id: ticketId });
  };

  const handlePhoneGenerate = () => {
    if (!phoneNo || phoneNo.trim() === "") {
      setValidationDialog({
        open: true,
        message: "Please enter a Phone Number to generate QR tickets",
      });
      return;
    }
    if (!ticketCount || ticketCount <= 0) {
      setValidationDialog({
        open: true,
        message: "Please enter a valid Ticket Count (greater than 0)",
      });
      return;
    }
    handleShortIdByPhone({
      phone_no: phoneNo,
      count: ticketCount,
    });
    handleGenerateQR({
      phone_no: phoneNo,
      ticket_count: ticketCount,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(45deg, #2196F3, #9C27B0)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 2,
          }}
        >
          QR Ticket Generator
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Generate and manage your QR tickets effortlessly
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Ticket ID Section */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <QrCodeIcon sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" fontWeight={600}>
                  Generate by Ticket ID
                </Typography>
              </Box>

              <TextField
                value={ticketId}
                onChange={(e) => {
                  setTicketId(e.target.value);
                  setQrTicketsData({ total_coins: 0, tickets: [] });
                }}
                placeholder="Enter ticket ID"
                variant="outlined"
                fullWidth
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "white" },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    "&::placeholder": { color: "rgba(255,255,255,0.7)" },
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handleTicketIdGenerate}
                fullWidth
                sx={{
                  py: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                Generate QR Tickets
              </Button>
            </CardContent>
          </Card>
        </Grid>
        {/* Phone & Count Section */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              color: "white",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PhoneIcon sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" fontWeight={600}>
                  Generate by Phone
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={phoneNo}
                    onChange={(e) => {
                      setPhoneNo(e.target.value);
                      setAllTickets([]);
                    }}
                    placeholder="Phone Number"
                    variant="outlined"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.5)",
                        },
                        "&.Mui-focused fieldset": { borderColor: "white" },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        "&::placeholder": { color: "rgba(255,255,255,0.7)" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={ticketCount}
                    type="number"
                    onChange={(e) => {
                      setTicketCount(e.target.value);
                      setAllTickets([]);
                    }}
                    placeholder="Ticket Count"
                    variant="outlined"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.5)",
                        },
                        "&.Mui-focused fieldset": { borderColor: "white" },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        "&::placeholder": { color: "rgba(255,255,255,0.7)" },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                onClick={handlePhoneGenerate}
                fullWidth
                sx={{
                  py: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                Generate QR Tickets
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Error Display */}
      {error?.error && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Chip
            label={error.error}
            color="error"
            sx={{
              py: 1,
              px: 2,
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          />
        </Box>
      )}

      {/* Validation Alert Dialog */}
      <Dialog
        open={validationDialog.open}
        onClose={() => setValidationDialog({ open: false, message: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <WarningAmberIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Input Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {validationDialog.message}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setValidationDialog({ open: false, message: "" })}
            fullWidth
            sx={{
              py: 1.5,
              bgcolor: "rgba(255,255,255,0.2)",
              borderRadius: 2,
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              fontWeight: 600,
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog
        open={qrTicketsData.tickets.length > 0}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 4, position: "relative" }}>
          {/* Close Button */}
          <IconButton
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* QR Code Display */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
              QR Ticket
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.95)",
                color: "black",
                display: "inline-block",
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {getDisplayText()}
              </Typography>

              <Box
                sx={{
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 2,
                  display: "inline-block",
                  border: "3px solid #f0f0f0",
                  "& img": {
                    backgroundColor: "white",
                    padding: "8px",
                    borderRadius: "4px",
                  },
                }}
              >
                <img
                  id="qrCodeImage"
                  src={qrTicketsData.tickets?.[current]?.qr}
                  alt="QR Code"
                  style={{
                    width: "200px",
                    height: "200px",
                    display: "block",
                    backgroundColor: "white",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            </Paper>
            {/* Download Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                mt: 3,
                bgcolor: "#2196F3",
                "&:hover": { bgcolor: "#1976D2" },
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                display: "block",
                margin: "24px auto 0",
              }}
              onClick={async () => {
                try {
                  const qrUrl = qrTicketsData.tickets?.[current]?.qr;
                  const currentTicketIdentifier = getDisplayText();

                  if (!qrUrl) {
                    console.error("QR code URL is not available.");
                    return;
                  }

                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");

                  const qrImage = new Image();
                  qrImage.src = qrUrl;

                  qrImage.onload = () => {
                    ctx.font = "bold 16px Arial";
                    const textMetrics = ctx.measureText(
                      currentTicketIdentifier,
                    );
                    const textWidth = textMetrics.width;
                    const textHeight = 16;

                    const padding = 20;
                    const spacing = 20;

                    canvas.width =
                      qrImage.width + textWidth + spacing + padding * 2;
                    canvas.height =
                      Math.max(qrImage.height, textHeight) + padding * 2;

                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    const qrX = padding;
                    const qrY = (canvas.height - qrImage.height) / 2;
                    ctx.drawImage(qrImage, qrX, qrY);

                    ctx.font = "bold 16px Arial";
                    ctx.fillStyle = "black";
                    ctx.textAlign = "left";

                    const textX = qrX + qrImage.width + spacing;
                    const textY = canvas.height / 2 + textHeight / 4;

                    ctx.fillText(currentTicketIdentifier, textX, textY);

                    canvas.toBlob((blob) => {
                      const blobUrl = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = blobUrl;
                      link.setAttribute(
                        "download",
                        `QR_Ticket_${currentTicketIdentifier}.png`,
                      );
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(blobUrl);
                    }, "image/png");
                  };
                } catch (error) {
                  console.error("Error downloading QR code:", error);
                }
              }}
            >
              Download QR
            </Button>
          </Box>

          {/* Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "rgba(255,255,255,0.1)",
              borderRadius: 2,
              p: 2,
            }}
          >
            <IconButton
              onClick={onPrev}
              disabled={current === 0}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={600}>
                {current + 1} of {qrTicketsData.tickets?.length ?? 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Tickets Generated
              </Typography>
            </Box>

            <IconButton
              onClick={onNext}
              disabled={current === qrTicketsData.tickets.length - 1}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default GetQRTickets;
